import { Optional } from "common/utils/types";

import { IAddress, Features, PatientKind } from ".";

/** matches clinics BasicClinicSerializer */
export interface IBaseClinic {
  id: number;
  name: string;
  logo: Optional<string>;
  primary_address: Optional<IAddress>;
  features: Features;
  primary_tag_id: number;
  vetcove_buyer_id: number;
  is_launched: boolean;
}

/** matches clinics BasicOnboardingClinicSerializer */
export interface BasicOnboardingClinic {
  id: number;
  name: string;
  logo: Optional<string>;
  primary_address: Optional<IAddress>;
  features: Features;
  primary_tag_id: number;
  vetcove_buyer_id: number;
  is_launched: boolean;
  onboarding_links_sent_at: string | null;
  pims_stripe_account_signup_complete: boolean;
}

/** matches clinics BasicPricingGroupSerializer */
export interface IBasePricingGroup {
  id: number;
  name: string;
  is_self_managed: boolean;
  description: string;
}

/** matches clinics ClinicPrescriptionLabelDimensionsSerializer */
export interface IClinicPrescriptionLabelDimensions {
  id: number;
  pims_name: string;
  width: string;
  height: string;
}

/** matches clinics SpecialtyDesignations */
export const SpecialtyDesignations = {
  EquineOnly: "EQUINE_ONLY",
  FelineOnly: "FELINE_ONLY",
} as const;
export type SpecialtyDesignations =
  (typeof SpecialtyDesignations)[keyof typeof SpecialtyDesignations];

/** matches clinics ClinicBusinessType */
export const BusinessTypes = {
  Cooperative: "COOPERATIVE",
  Corporation: "CORPORATION",
  LimitedLiabilityCorporation: "LIMITED_LIABILITY_CORPORATION",
  NonProfitCorporation: "NON_PROFIT_CORPORATION",
  Partnership: "PARTNERSHIP",
  SoleProprietorship: "SOLE_PROPRIETORSHIP",
} as const;
export type BusinessTypes = (typeof BusinessTypes)[keyof typeof BusinessTypes];

/** matches clinics ClinicOwnershipType */
export const BusinessOwnershipTypes = {
  Private: "PRIVATE",
  Public: "PUBLIC",
  NonProfit: "NON_PROFIT",
  Government: "GOVERNMENT",
} as const;
export type BusinessOwnershipTypes =
  (typeof BusinessOwnershipTypes)[keyof typeof BusinessOwnershipTypes];

export const BusinessTypesDisplayNames: Record<BusinessTypes | string, string> = {
  [BusinessTypes.Cooperative]: "Cooperative",
  [BusinessTypes.Corporation]: "Corporation",
  [BusinessTypes.LimitedLiabilityCorporation]: "Limited Liability Corporation",
  [BusinessTypes.NonProfitCorporation]: "Non-profit Corporation",
  [BusinessTypes.Partnership]: "Partnership",
  [BusinessTypes.SoleProprietorship]: "Sole Proprietorship",
};

export const BusinessOwnershipTypesDisplayNames: Record<BusinessOwnershipTypes | string, string> = {
  [BusinessOwnershipTypes.Private]: "Private",
  [BusinessOwnershipTypes.Public]: "Public",
  [BusinessOwnershipTypes.NonProfit]: "Non-profit",
  [BusinessOwnershipTypes.Government]: "Government",
};

export const SpecialtyDesignationsDisplayNames: Record<SpecialtyDesignations | string, string> = {
  // eslint-disable-next-line no-useless-computed-key
  [""]: "General practice / other",
  [SpecialtyDesignations.EquineOnly]: "Equine-only clinic",
  [SpecialtyDesignations.FelineOnly]: "Feline-only clinic",
};

export const SpecialtyDesignationToPatientKinds: Record<SpecialtyDesignations, PatientKind> = {
  [SpecialtyDesignations.EquineOnly]: PatientKind.Equine,
  [SpecialtyDesignations.FelineOnly]: PatientKind.Feline,
};

/** matches clinics ClinicCRUDSerializer */
export interface IDetailClinic extends IBaseClinic {
  client_display_name: string;
  base_client_url: string;
  store_url: string;
  hours: IStoreHours;
  phone_number: Optional<string>;
  email: Optional<string>;
  active_pricing_group: IBasePricingGroup;
  allow_urgent_pickups: boolean;
  urgency_fee: number;
  should_review_fulfillments: boolean;
  pickup_instructions: string;
  active_prescription_label: IClinicPrescriptionLabelDimensions;
  has_employee_pricing: boolean;
  employee_markup_percentage: Optional<string>;
  slug: string;
  include_warning_on_prescription_label: boolean;
  specialty_designation: Optional<SpecialtyDesignations>;
  external_client_booking_url: string | null;
}

/** matches ActiveClinicUserDefaultClinicSerializer */
export type ActiveClinicUserDefaultClinic = {
  id: number;
  slug: string;
  name: string;
  timezone: string;
  logo: Optional<string>;
  primary_address: Optional<IAddress>;
  onboarding_links_sent_at: string | null;
  features: Features;
  primary_tag_id: number;
  vetcove_buyer_id: number;
  is_launched: boolean;
  client_display_name: string;
  base_client_url: string;
  store_url: string;
  hours: IStoreHours;
  phone_number: Optional<string>;
  email: Optional<string>;
  allow_urgent_pickups: boolean;
  urgency_fee: number;
  should_review_fulfillments: boolean;
  pickup_instructions: string;
  active_prescription_label: IClinicPrescriptionLabelDimensions;
  include_warning_on_prescription_label: boolean;
  specialty_designation: Optional<SpecialtyDesignations>;
  pims_stripe_account_signup_complete: boolean;
};

/** matches clinics ClinicBusinessInfoReadSerializer */
export interface PopulatedBusinessDetails {
  id: number;
  clinic_id: number;
  name: string;
  doing_business_as: string | null;
  business_type: BusinessTypes;
  ownership_type: BusinessOwnershipTypes;
  ein: string;
  ein_formatted: string;
  url: string;
  address: IAddress;
  primary_contact_first_name: string;
  primary_contact_last_name: string;
  primary_contact_job_title: string;
  primary_contact_phone_number: number;
  primary_contact_email: string;
}
export interface BusinessDetailsPlaceholder {
  url: string | null;
  address: IAddress | null;
  clinic_id?: never;
  name?: never;
  doing_business_as?: never;
  business_type?: never;
  ownership_type?: never;
  ein?: never;
  ein_formatted?: never;
  primary_contact_first_name?: never;
  primary_contact_last_name?: never;
  primary_contact_job_title?: never;
  primary_contact_phone_number?: never;
  primary_contact_email?: never;
}

/** matches clinics ClinicBusinessInfoView.get */
export type BusinessDetailsResponse = PopulatedBusinessDetails | BusinessDetailsPlaceholder;
/** matches clinics ClinicCRUDSerializer _writable_fields */
type ClinicDetailWrite = {
  client_display_name: string;
  phone_number: Optional<string>;
  email: Optional<string>;
  allow_urgent_pickups: boolean;
  urgency_fee: number;
  should_review_fulfillments: boolean;
  pickup_instructions: string;
  prescription_label: number;
  logo: Optional<string>;
};

export type ClinicDetailUpdate = Partial<ClinicDetailWrite>;

/** matches clients ClinicCommunicationNumberDetailSerializer */
export interface IClinicPhone {
  id: number;
  number: number;
  number_display: string;
}

export const Weekday = {
  Sunday: "SUNDAY",
  Monday: "MONDAY",
  Tuesday: "TUESDAY",
  Wednesday: "WEDNESDAY",
  Thursday: "THURSDAY",
  Friday: "FRIDAY",
  Saturday: "SATURDAY",
} as const;
export type Weekday = (typeof Weekday)[keyof typeof Weekday];

export const ORDERED_WEEKDAYS = [
  Weekday.Sunday,
  Weekday.Monday,
  Weekday.Tuesday,
  Weekday.Wednesday,
  Weekday.Thursday,
  Weekday.Friday,
  Weekday.Saturday,
];

export const INT_TO_WEEKDAY: Record<number, Weekday> = {
  0: Weekday.Sunday,
  1: Weekday.Monday,
  2: Weekday.Tuesday,
  3: Weekday.Wednesday,
  4: Weekday.Thursday,
  5: Weekday.Friday,
  6: Weekday.Saturday,
};

/**
 * Matches stores StoreHoursSerializer
 * note that optional fields come from StoreController not providing these fields
 * for the "closed" hours -- see StoreController._closed_store_day_hours
 */
export interface IHour {
  is_open: boolean;
  weekday_display: string;
  opening_hour: Optional<string>;
  closing_hour: Optional<string>;
  opening_hour_ui_string: Optional<string>;
  closing_hour_ui_string: Optional<string>;
}

/** Matches stores StoreController.get_hours_with_defaults return value */
export type IStoreHours = Record<Weekday, IHour>;

/** Matches expected query_params/request data for ClinicDelegateAPIView */
export type ClinicDelegateBody = {
  clinic: number;
};

/** Matches clinics constants StorefrontThemeLayout */
export const StorefrontThemeLayout = {
  DEFAULT: "DEFAULT",
  CUTOUT: "CUTOUT",
  HERO: "HERO",
  // custom
  BONDVET: "BONDVET",
  THRIVE: "THRIVE",
  GOODVETS: "GOODVETS",
} as const;
export type StorefrontThemeLayout =
  (typeof StorefrontThemeLayout)[keyof typeof StorefrontThemeLayout];

/** The client home page search */
export type ClientHomeSearch = {
  layout?: StorefrontThemeLayout;
  color?: number;
  logo?: string;
  image?: string;
  override_is_store_enabled?: boolean;
  preview?: boolean;
};

export const CLINIC_TAX_EXEMPTION_FILE_NAME = "tax_exemption_form";
export const CLINIC_W9_FILE_NAME = "w9_form";
